/**
 * Clipboard flavor of a paste source (task 4.3's classification seam; moved to
 * `/core` by task 5.5). Package-owned so the classifier carries no application
 * analytics type across the seam — the host maps these values onto its own
 * event property, and the app-side parity test pins the two unions equal.
 */
export type PasteFlavor = 'vscode_markdown' | 'vscode_code' | 'html' | 'plain_text';

/**
 * A VS Code / Monaco copy, keyed on the flavor's presence rather than on a
 * readable `mode` — a malformed payload is still a VS Code paste, and VS Code
 * omits the mode whenever it skips its rich-text flavors.
 */
export const isVsCodePaste = (data: DataTransfer): boolean =>
  Boolean(data.getData('vscode-editor-data'));

/**
 * Typed safe-failure envelope for an unreadable `vscode-editor-data` flavor,
 * mirroring `PasteParseFailure`. The only fields that may cross the reporting
 * seam: a stable code, the error class name, and the payload length — never the
 * payload itself, which is clipboard-derived and could carry PHI.
 */
export interface PasteFlavorDataFailure {
  code: 'paste_flavor_data_unreadable';
  /** `Error` class name, or `typeof` for non-Error throws — never the message. */
  errorName: string;
  /** Length of the `vscode-editor-data` payload — never the payload. */
  dataLength: number;
}

export type PasteFlavorDataFailureReporter = (failure: PasteFlavorDataFailure) => void;

/**
 * Language mode from a VS Code / Monaco copy's `vscode-editor-data` flavor.
 *
 * An unreadable payload falls back to `vscode_code`, indistinguishable from a
 * genuine non-markdown VS Code copy — so it silently skews `paste_flavor`
 * unless the host is told. The reporter is the only way out of this module
 * (`/core` carries no application logging; same contract as
 * `parse-pasted-markdown.ts`), and stays optional so classification itself
 * never depends on instrumentation.
 */
const vsCodeMode = (
  data: DataTransfer,
  onFlavorDataFailure?: PasteFlavorDataFailureReporter
): string | undefined => {
  const vscode = data.getData('vscode-editor-data');
  if (!vscode) return undefined;
  try {
    const { mode } = JSON.parse(vscode) as { mode?: unknown };
    return typeof mode === 'string' ? mode : undefined;
  } catch (error) {
    onFlavorDataFailure?.({
      code: 'paste_flavor_data_unreadable',
      errorName: error instanceof Error ? error.name : typeof error,
      dataLength: vscode.length,
    });
    return undefined;
  }
};

/**
 * Classify a paste by clipboard flavor. Reflects the copy SOURCE, not the DOM
 * outcome — keyed on flavor presence so the buckets match what the host's VS
 * Code paste handler intercepts.
 *
 * Reads flavors only; never the pasted content, and returns nothing derived
 * from it beyond the bucket.
 *
 * Pass `onFlavorDataFailure` to observe the one bucket that can be wrong: an
 * unreadable VS Code payload lands in `vscode_code` regardless.
 */
export const classifyPasteFlavor = (
  data: DataTransfer,
  onFlavorDataFailure?: PasteFlavorDataFailureReporter
): PasteFlavor => {
  if (isVsCodePaste(data)) {
    return vsCodeMode(data, onFlavorDataFailure) === 'markdown'
      ? 'vscode_markdown'
      : 'vscode_code';
  }
  return data.getData('text/html') ? 'html' : 'plain_text';
};

/**
 * Length of the pasted content: the plain-text flavor when present, otherwise
 * the text content of the HTML flavor — raw markup would inflate the measure.
 * Independent of flavor classification: an `html`-flavor paste still measures
 * its plain-text rendition when the source provides one.
 *
 * A length, never the text — the only safe measure to cross a telemetry seam
 * (design D6/D8).
 */
export const pastedContentLength = (data: DataTransfer): number => {
  const plain = data.getData('text/plain');
  if (plain) return plain.length;
  // Same inert-document parse as `normalizePastedFormatting`: no browsing
  // context, so measuring can't execute pasted markup.
  const container = document.implementation.createHTMLDocument('').createElement('div');
  container.innerHTML = data.getData('text/html');
  return container.textContent?.length ?? 0;
};
